import {
  AccountNumberAccountNumber,
  AccountNumberListAccountNumbersResponse,
  AccountNumberServiceCreateAccountNumberPayload,
  AccountNumberServiceListAccountNumbersParams,
  BankAccountListResponse,
  BankAccountServiceListBankAccountsParams,
  BankAccountWithDetails,
} from '~/typings/API';
import { convertValues } from '~/util/convertValues';

import { client } from './client';

type BankAccount = BankAccountWithDetails;
type AccountNumber = AccountNumberAccountNumber;
type ActivityType = 'Realtime' | 'Intl Wire' | 'Check' | 'ACH' | 'Wire';

export interface ActivityLineItem {
  activityLineItemId: string;
  settlementDate: string;
  type: ActivityType;
  bankAccountId: string;
  transactionId: string;
  description: string;
  amount: number;
  runningBalance: number;
}

export interface ListBankAccountActivitiesRequest extends PaginationQueryParams {
  effectiveAtGt?: string;
  effectiveAtLt?: string;
  effectiveAtGte?: string;
  effectiveAtLte?: string;
}

export interface ListBankAccountActivitiesResponse {
  activityLineItems: ActivityLineItem[];
  hasMore: boolean;
}

export type { ActivityType, BankAccount, AccountNumber, BankAccountListResponse };
export type CreateAccountNumber = AccountNumberServiceCreateAccountNumberPayload;
export type UpdateAccountNumber = AccountNumberServiceCreateAccountNumberPayload;
export type BankAccountFilterParams = BankAccountServiceListBankAccountsParams & { platformId?: string };

const bankAccountCast = (bankAccount: BankAccount) => {
  if (!bankAccount) {
    return {};
  }
  const { defaultAccountNumber, routingNumber } = bankAccount;
  return {
    defaultAccountNumber,
    routingNumber,
  };
};

const accountNumberCast = (accountNumber: AccountNumber) => {
  if (!accountNumber) {
    return {};
  }
  return {
    accountNumber: accountNumber.accountNumber,
    routingNumber: accountNumber.routingNumber,
  };
};

export class BankAccountRepository {
  static async get(request: GetRequestType & { mode?: 'live' | 'sandbox' }) {
    const { mode, platformId, ...requestParams } = request;
    return client
      .get<
        unknown,
        BankAccount
      >(`/bank-accounts/${requestParams.id}`, { platformId, sandbox: mode ? mode === 'sandbox' : undefined })
      .then((response) => convertValues<BankAccount>(response, undefined, bankAccountCast(response)));
  }

  static async getByIds(request: { ids: string[]; mode?: 'live' | 'sandbox'; platformId?: string }) {
    const { ids, mode, platformId } = request;
    if (ids.length === 0) {
      return { bankAccounts: [], hasMore: false };
    }
    return client
      .get<BankAccountFilterParams, BankAccountListResponse>('/bank-accounts', {
        query: { bankAccountIds: ids.join(',') },
        platformId,
        sandbox: mode ? mode === 'sandbox' : undefined,
      })
      .then((response) => ({
        bankAccounts: Array.isArray(response?.bankAccounts)
          ? response.bankAccounts.map((e) => convertValues<BankAccount>(e, undefined, bankAccountCast(e)))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }

  static async getAll(request?: Partial<BankAccountFilterParams> & { mode?: 'live' | 'sandbox' }) {
    const { platformId, mode, ...query } = request ?? {};
    return client
      .get<
        BankAccountFilterParams,
        BankAccountListResponse
      >('/bank-accounts', { query, platformId, sandbox: mode ? mode === 'sandbox' : undefined })
      .then((response) => ({
        bankAccounts: Array.isArray(response?.bankAccounts)
          ? response.bankAccounts.map((e) => convertValues<BankAccount>(e, undefined, bankAccountCast(e)))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }

  static async delete(id: string) {
    return client.delete<object, object>(`/bank-accounts/${id}`);
  }

  static async createAccountNumber(bankAccountId: string, accountNumber: CreateAccountNumber) {
    return client
      .post<CreateAccountNumber, AccountNumber>(`/bank-accounts/${bankAccountId}/account-numbers`, accountNumber)
      .then((response) => convertValues<AccountNumber>(response, undefined, accountNumberCast(response)));
  }

  static async updateAccountNumber(accountNumberId: string, accountNumber: UpdateAccountNumber) {
    return client
      .patch<UpdateAccountNumber, AccountNumber>(`/account-numbers/${accountNumberId}`, accountNumber)
      .then((response) => convertValues<AccountNumber>(response, undefined, accountNumberCast(response)));
  }

  static async getAccountNumber(request: GetRequestType) {
    const { platformId, ...requestParams } = request;
    return client
      .get<unknown, AccountNumber>(`/account-numbers/${requestParams.id}`, { platformId })
      .then((response) => convertValues<AccountNumber>(response, undefined, accountNumberCast(response)));
  }

  static async getAllAccountNumber(
    bankAccountId: string,
    query?: Partial<AccountNumberServiceListAccountNumbersParams>
  ) {
    return client
      .get<AccountNumberServiceListAccountNumbersParams, AccountNumberListAccountNumbersResponse>(
        `/bank-accounts/${bankAccountId}/account-numbers`,
        {
          query,
        }
      )
      .then((response) => ({
        accountNumbers: Array.isArray(response?.accountNumbers)
          ? response.accountNumbers.map((e) => convertValues<AccountNumber>(e, undefined, accountNumberCast(e)))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }

  static async getActivity(bankAccountId: string, query: ListBankAccountActivitiesRequest) {
    return client
      .get<Omit<ListBankAccountActivitiesRequest, 'bankAccountId'>, ListBankAccountActivitiesResponse>(
        `/bank-accounts/${bankAccountId}/activities`,
        {
          query,
        }
      )
      .then((response) => ({
        activityLineItems: Array.isArray(response?.activityLineItems)
          ? response.activityLineItems.map((e) => convertValues<ActivityLineItem>(e))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }
}
